import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  useRouter,
} from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { Hub } from "aws-amplify/utils"
import { Suspense, useEffect } from "react"
import { RouterProvider } from "react-aria-components"

import api from "@/utilities/api"

import ErrorComponent from "@/globalComponents/organisms/ErrorComponent"
import Footer from "@/globalComponents/organisms/navigation/Footer"
import TopNavigationBar from "@/globalComponents/organisms/navigation/TopNavigationBar"
import RootNotFound from "@/globalComponents/organisms/RootNotFound"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { DocumentThemeMode, ScannedArea } from "@/utilities/types/local"

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  setScannedArea: (scannedArea: ScannedArea) => void
  setDocumentThemeMode: (mode: DocumentThemeMode) => void
}>()({
  head: () => ({
    meta: [
      {
        title: "Tap 'n Fly Parking Solutions",
      },
    ],
  }),
  beforeLoad: async ({ context }) => {
    context.setDocumentThemeMode("root")
    const session = await api.auth.fetchAuthSession()
    if (session.error || !session.cognitoIDToken) {
      sessionStorage.clear()
      return { isAuthenticated: false }
    }
    return { isAuthenticated: true }
  },
  component: RootComponent,
  errorComponent: (opts) => (
    <main>
      <ErrorComponent {...opts} resolution="goHomeHome" />
    </main>
  ),
  notFoundComponent: (opts) => {
    return <RootNotFound {...opts} />
  },
})

function RootComponent() {
  const query = useQueryClient()
  const router = useRouter()

  useEffect(() => {
    const hubListener = Hub.listen("auth", ({ payload }) => {
      const { event } = payload
      switch (event) {
        case "signedIn":
          query.invalidateQueries({
            queryKey: meQueryOptions().queryKey,
            refetchType: "all",
          })
          // We want to allow the auth form to invalidate the router and handle the navigation itself
          break
        case "signedOut":
          query.invalidateQueries({
            queryKey: meQueryOptions().queryKey,
            refetchType: "all",
          })
          router.invalidate()
          break
      }
    })

    return () => {
      hubListener()
    }
  }, [])

  return (
    <RouterProvider
      navigate={(to, options) => router.navigate({ to, ...options })}
      useHref={(to) => router.buildLocation({ to }).href}
    >
      <HeadContent />
      <TopNavigationBar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <Suspense>
        <TanStackRouterDevtools position="bottom-left" />
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-left"
        />
      </Suspense>
    </RouterProvider>
  )
}
